import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { authApi } from './api';
import { useAuthStore } from '@/lib/auth/authStore';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { usePlanContext } from '@onxite/plan-sdk';

export function OtpPage() {
  const pendingEmail = useAuthStore((s) => s.pendingEmail);
  const token = useAuthStore((s) => s.token);
  const setSession = useAuthStore((s) => s.setSession);
  const setPendingEmail = useAuthStore((s) => s.setPendingEmail);
  const navigate = useNavigate();
  const t = useT();
  const { reportPlanAlert } = usePlanContext();
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(30);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const verify = useMutation({
    mutationFn: (otp: string) => authApi.verifyOtp(pendingEmail!, otp),
    onSuccess: (data) => {
      setSession({
        token: data.access_token,
        refreshToken: data.refresh_token,
        user: data.user,
        role: data.role,
      });
      setPendingEmail(null);
      reportPlanAlert((data as any).planAlert ?? null);
      toast({ tone: 'success', title: data.isNewUser ? t('otp.toast.welcome') : t('otp.toast.signedIn') });
      navigate('/');
    },
    onError: () => toast({ tone: 'error', title: t('otp.toast.invalidExpired') }),
  });

  const resend = useMutation({
    mutationFn: () => authApi.sendOtp(pendingEmail!),
    onSuccess: () => {
      setSecondsLeft(30);
      toast({ tone: 'info', title: t('otp.toast.newCodeSent'), description: t('otp.toast.newCodeBody') });
    },
  });

  useEffect(() => {
    if (code.length === 6) {
      verify.mutate(code);
    }
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  // After successful verify, setPendingEmail(null) fires a re-render while the
  // imperative navigate('/') from onSuccess is still in flight. Without the
  // token check this guard would race the navigation and bounce the user back
  // to /login despite having a valid session.
  if (!pendingEmail) return <Navigate to={token ? '/' : '/login'} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={() => {
            setPendingEmail(null);
            navigate('/login');
          }}
          className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
        >
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </button>

        <div className="mb-6 grid h-16 w-16 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]">
          <Mail className="h-7 w-7" />
        </div>

        <h1 className="text-2xl font-bold">{t('otp.checkInbox')}</h1>
        <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
          {t('otp.subtitlePrefix')}{' '}
          <span className="font-semibold text-[var(--color-accent)]">{pendingEmail}</span>
        </p>

        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="······"
          className="mt-8 h-16 w-full rounded-xl border border-[var(--color-border-default)] bg-black/40 text-center font-mono text-3xl font-bold tracking-[0.6em] text-[var(--color-accent)] placeholder:text-[var(--color-fg-disabled)] focus:border-[var(--color-accent)] focus:outline-none"
        />

        <Button
          type="button"
          className="mt-6 w-full"
          size="lg"
          loading={verify.isPending}
          disabled={code.length !== 6}
          onClick={() => verify.mutate(code)}
        >
          {t('otp.verifyCode')}
        </Button>

        <div className="mt-6 text-center text-sm text-[var(--color-fg-muted)]">
          {t('otp.didntReceive')}{' '}
          <button
            type="button"
            disabled={secondsLeft > 0 || resend.isPending}
            onClick={() => resend.mutate()}
            className="font-semibold text-[var(--color-accent)] disabled:text-[var(--color-fg-subtle)]"
          >
            {secondsLeft > 0 ? t('otp.resendIn', { seconds: secondsLeft }) : t('otp.resendNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
